import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth';

/**
 * Страновые варианты текстов интерфейса (в первую очередь — подсказки в полях).
 *
 * Зачем отдельно от языка. Язык ≠ страна: большинство эмиратских тенантов Fixty
 * сидят на русском интерфейсе. Если бы примеры адресов жили прямо в ru.json,
 * дубайская компания получила бы «Москва, ул. Ленина» вместо «Dubai, Jumeirah».
 * Поэтому язык выбирает ЯЗЫК строки, а страна организации — её СОДЕРЖАНИЕ.
 *
 * Как устроено. Базовый ключ — эмиратский (страна по умолчанию AE, см.
 * backend/config/countries.js). Для страны, где пример должен отличаться,
 * рядом кладётся ключ с суффиксом `_<КОД>`:
 *
 *   "namePlaceholder":    "Апартаменты на Jumeirah"
 *   "namePlaceholder_RU": "Квартира на Ленинском"
 *
 * Резолвер — штатный фоллбэк i18next по массиву ключей: берётся первый
 * существующий. Значит для страны, где пример универсален, ничего дописывать
 * не нужно — вернётся базовый ключ. Ключи `_RU` обязаны существовать в ОБОИХ
 * локалях (ru.json и en.json) — иначе EN-интерфейс РФ-тенанта молча съедет на
 * эмиратский пример.
 *
 * Использование:
 *   const { ct } = useCountryText();
 *   <input placeholder={ct('properties.namePlaceholder')} />
 */
export function useCountryText() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const country = String(user?.org_country || 'AE').toUpperCase();

  // Смену языка ловим через сам `t`: useTranslation отдаёт новую функцию на
  // событие languageChanged, поэтому мемо пересчитывается без i18n.language
  // в зависимостях (мутабельное поле объекта туда класть нельзя).
  const ct = useCallback((key, opts) => t([`${key}_${country}`, key], opts), [t, country]);

  return { ct, country };
}
